import React, { useEffect, useRef } from "react"
import { MapPin } from "lucide-react"
import Accordion from "./Accordion.jsx"
import InputField from "./InputField.jsx"
import { OrderFormCheckbox } from "./EnterKeyControls.jsx"
import { isDeliverySectionComplete } from "./sectionCompletion.js"
import {
    ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
} from "./orderFormAccordionConstants.js"
import { focusElement } from "../utils/scrollFocusedElement.js"

/**
 * Pickup location dropdown options come from the parent (`getPickupLocations` in shared `utils/locations.js`).
 *
 * @param {{
 *   value: { method: string, pickupLocationId: number | null, holdForPickup?: boolean },
 *   locations: Array<{ id: unknown, name: string }>,
 *   containers: string,
 *   onContainersChange: (containers: string) => void,
 *   onChange: (v: { method: string, pickupLocationId: number | null, holdForPickup?: boolean }) => void,
 *   onNext: () => void,
 *   title?: string,
 *   isOpen?: boolean,
 *   touched?: boolean,
 *   statusLabel?: string | null,
 *   disabled?: boolean,
 *   onHeaderClick?: () => void,
 *   isActive?: boolean,
 *   activationFocusMode?: "first" | "nextRequired" | null,
 *   onActivationFocusConsumed?: () => void,
 *   activationFocusDelayMs?: number,
 *   fieldErrors?: Record<string, string> | null,
 *   showFieldErrors?: boolean,
 *   hasErrors?: boolean,
 * }} props
 */
export default function DeliverySection({
    value,
    locations,
    containers,
    onContainersChange,
    onChange,
    onNext,
    title = "2. Delivery Method",
    isOpen,
    touched = false,
    statusLabel = null,
    disabled = false,
    onHeaderClick,
    isActive = false,
    activationFocusMode = null,
    onActivationFocusConsumed,
    activationFocusDelayMs = 350,
    fieldErrors = null,
    showFieldErrors = false,
    hasErrors = false,
}) {
    const shipBtnRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const pickupBtnRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const pickupSelectRef = useRef(/** @type {HTMLSelectElement | null} */(null))
    const nextBtnRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const valueRef = useRef(value)

    valueRef.current = value

    const isCompleted = isDeliverySectionComplete(value) && Boolean(touched)

    useEffect(() => {
        if (
            !isActive ||
            activationFocusMode == null ||
            !onActivationFocusConsumed
        ) {
            return
        }
        const mode = activationFocusMode
        const tmr = window.setTimeout(() => {
            /** @type {HTMLElement | null} */
            let el = null
            if (mode === "first") {
                el = shipBtnRef.current
            } else {
                const v = valueRef.current
                const pickupNeedLoc =
                    v.method === "pickup" &&
                    v.pickupLocationId == null
                if (pickupNeedLoc) {
                    el = pickupSelectRef.current
                } else if (isDeliverySectionComplete(v)) {
                    if (v.method === "pickup") {
                        el = pickupBtnRef.current
                    } else {
                        el = shipBtnRef.current
                    }
                }
            }
            focusElement(el, { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            onActivationFocusConsumed()
        }, activationFocusDelayMs)
        return () => window.clearTimeout(tmr)
    }, [
        isActive,
        activationFocusMode,
        activationFocusDelayMs,
        onActivationFocusConsumed,
    ])

    function commitMethodAndAdvance(method) {
        const next =
            method === "ship" ?
                { ...valueRef.current, method, holdForPickup: false } :
                { ...valueRef.current, method }
        onChange(next)
        window.setTimeout(() => {
            const v = valueRef.current
            if (method === "pickup" && v.pickupLocationId == null) {
                focusElement(pickupSelectRef.current, { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            } else {
                focusElement(nextBtnRef.current, { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            }
        }, 0)
    }

    /**
     * @param {React.KeyboardEvent<HTMLButtonElement>} e
     * @param {"ship" | "pickup"} method
     */
    function handleMethodKeyDown(e, method) {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault()
            if (method === "ship") {
                pickupBtnRef.current?.focus({ preventScroll: true })
            }
            return
        }
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault()
            if (method === "pickup") {
                shipBtnRef.current?.focus({ preventScroll: true })
            }
            return
        }
        const activate =
            e.key === "Enter" ||
            (e.key === "Tab" && !e.shiftKey)
        if (activate) {
            e.preventDefault()
            commitMethodAndAdvance(method)
        }
    }

    return (
        <Accordion
            title={title}
            isActive={isOpen ?? isActive}
            isCompleted={isCompleted}
            statusLabel={statusLabel}
            disabled={disabled}
            onClick={onHeaderClick}
            hasErrors={hasErrors}
        >
            <div className="row g-4">
            <div className="col-12">
                <div
                    className="row g-4"
                    role="radiogroup"
                    aria-label="Delivery method"
                >
                    <div className="col-12 col-md-6">
                        <button
                            ref={shipBtnRef}
                            type="button"
                            role="radio"
                            aria-checked={value.method === "ship"}
                            onClick={() =>
                                onChange({
                                    ...value,
                                    method: "ship",
                                    holdForPickup: false,
                                })
                            }
                            onKeyDown={(e) => handleMethodKeyDown(e, "ship")}
                            className={`delivery-method-btn w-100 p-4 rounded d-flex flex-column align-items-center ${value.method === "ship" ?
                                "delivery-method-btn--selected" :
                                ""
                                }`}
                            style={{ cursor: "pointer" }}
                        >
                            <MapPin className="text-primary mb-3" size={32} />
                            <span className="fw-semibold">Ship to Address</span>
                        </button>
                    </div>
                    <div className="col-12 col-md-6">
                        <button
                            ref={pickupBtnRef}
                            type="button"
                            role="radio"
                            aria-checked={value.method === "pickup"}
                            onClick={() =>
                                onChange({ ...value, method: "pickup" })
                            }
                            onKeyDown={(e) => handleMethodKeyDown(e, "pickup")}
                            className={`delivery-method-btn w-100 p-4 rounded d-flex flex-column align-items-center ${value.method === "pickup" ?
                                "delivery-method-btn--selected" :
                                ""
                                }`}
                            style={{ cursor: "pointer" }}
                        >
                            <MapPin className="text-primary mb-3" size={32} />
                            <span className="fw-semibold">Pick up at Location</span>
                        </button>
                    </div>
                </div>
            </div>

            {value.method === "pickup" && (
                <div className="col-12">
                    <div className="mb-3">
                        <label className="form-label" htmlFor="delivery-pickup-location">
                            Select Pickup Location
                        </label>
                        <select
                            id="delivery-pickup-location"
                            ref={pickupSelectRef}
                            value={value.pickupLocationId == null ? "" : String(value.pickupLocationId)}
                            onChange={(e) => {
                                const raw = e.target.value
                                onChange({
                                    ...value,
                                    pickupLocationId: raw === "" ? null : Number(raw),
                                })
                            }}
                            className={`form-select${showFieldErrors && fieldErrors?.pickupLocationId ? " is-invalid" : ""}`}
                        >
                            <option value="">Select a location</option>
                            {locations.map((location) => (
                                <option key={String(location.id)} value={String(location.id)}>
                                    {String(location.name ?? "")}
                                </option>
                            ))}
                        </select>
                        {showFieldErrors && fieldErrors?.pickupLocationId ? (
                            <div className="text-danger small mt-1">{fieldErrors.pickupLocationId}</div>
                        ) : null}
                    </div>

                    <div className="form-check mb-3">
                        <OrderFormCheckbox
                            id="delivery-hold-for-pickup"
                            className={`form-check-input${showFieldErrors && fieldErrors?.holdForPickup ? " is-invalid" : ""}`}
                            checked={Boolean(value.holdForPickup)}
                            onCheckedChange={(checked) =>
                                onChange({ ...value, holdForPickup: Boolean(checked) })
                            }
                        />
                        <label
                            className="form-check-label"
                            htmlFor="delivery-hold-for-pickup"
                        >
                            Hold for pick up
                        </label>
                        {showFieldErrors && fieldErrors?.holdForPickup ? (
                            <div className="text-danger small mt-1">{fieldErrors.holdForPickup}</div>
                        ) : null}
                    </div>

                    {value.holdForPickup && (
                        <InputField
                            id="processing-containers"
                            label="Number of Containers/Boxes"
                            type="number"
                            min="0"
                            style={{ maxWidth: "200px" }}
                            value={containers}
                            error={showFieldErrors ? fieldErrors?.containers : undefined}
                            onKeyDown={(e) => {
                                if (e.key === "-") {
                                    e.preventDefault()
                                }
                            }}
                            onChange={(e) => {
                                const raw = e.target.value
                                if (raw === "") {
                                    onContainersChange("")
                                    return
                                }
                                const n = Number(raw)
                                onContainersChange(
                                    Number.isFinite(n) ? String(Math.max(0, n)) : ""
                                )
                            }}
                        />
                    )}
                </div>
            )}

            <div className="col-12">
                <div className="d-flex justify-content-end">
                    <button
                        ref={nextBtnRef}
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={onNext}
                    >
                        Next
                    </button>
                </div>
            </div>
            </div>
        </Accordion>
    )
}
